import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { chatApi } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { ERROR_CODES } from "@/constants";
import {
  ChatMessage,
  ChatState,
  ChatStreamChunk,
  ChatSession,
} from "@/features/chat/types";
import {
  LocalUploadedFile,
  MultipleFileUploadResponse,
  ApiError,
} from "@/types";
import { useTranslation } from "react-i18next";
import { shouldHideMessage } from "@/helpers/messageFilter";
import { translateBackendMessage } from "@/helpers/backendMessageHelper";
import { isOrgDisclaimerRequired } from "@/helpers";
import { voiceCoordinator } from "@/features/chat/helpers";
import {
  AnswerForm,
  buildStreamAnswerForm,
  isAnswerFormMenuEnabled,
} from "@/features/chat/helpers";

interface ChatContextType {
  // State
  messages: ChatMessage[];
  currentSessionId: string | null;
  currentDbSessionId: number | null;
  isLoading: boolean;
  isLoadingSession: boolean;
  isStreaming: boolean;
  error: string | null;

  // Actions
  sendMessage: (
    message: string,
    fileIds?: string[],
    forceNewSession?: boolean,
    answerForm?: AnswerForm
  ) => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
  stopStreaming: () => void;
  clearMessages: () => void;
  setCurrentSession: (sessionId: string | null) => void;
  finalizeMessageStreaming: (messageId: string) => void; // Finalize specific message

  // Session management
  createNewSession: () => void;
  loadSession: (sessionId: string) => Promise<void>;
  loadMockSession: () => void;
  startChatWithIntroMessage: (
    introMessage: string,
    forceNewSession?: boolean
  ) => Promise<void>;
  sessions: ChatSession[];
  loadSessions: () => Promise<void>;

  // File management
  selectedFiles: LocalUploadedFile[];
  addFiles: (files: File[]) => void;
  removeSelectedFile: (fileId: string) => void;
  clearSelectedFiles: () => void;
  setFileStatus: (
    fileId: string,
    status: LocalUploadedFile["status"],
    error?: string
  ) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [state, setState] = useState<ChatState>({
    messages: [],
    currentSessionId: null,
    currentDbSessionId: null,
    isLoading: false,
    isLoadingSession: false,
    isStreaming: false,
    error: null,
  });

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [fakeSessions, setFakeSessions] = useState<ChatSession[]>([]); // Store fake sessions separately
  const [selectedFiles, setSelectedFiles] = useState<LocalUploadedFile[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const finalizationTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Track finalization timeout
  const introMessageProcessingRef = useRef<boolean>(false);
  const previousAuthStateRef = useRef<boolean>(false); // Track previous auth state
  const { showToast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const disclaimerBlocksChat = isOrgDisclaimerRequired(user?.org_disclaimer);

  // Clear chat state when user logs out
  useEffect(() => {
    // If user was authenticated but now is not (logout detected)
    if (previousAuthStateRef.current && !isAuthenticated) {
      // Stop any ongoing streaming
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Clear any pending finalization timeout
      if (finalizationTimeoutRef.current) {
        clearTimeout(finalizationTimeoutRef.current);
        finalizationTimeoutRef.current = null;
      }

      // Reset all chat state to initial values
      setState({
        messages: [],
        currentSessionId: null,
        currentDbSessionId: null,
        isLoading: false,
        isLoadingSession: false,
        isStreaming: false,
        error: null,
      });

      // Clear sessions
      setSessions([]);
      setFakeSessions([]);
      setIsLoadingSessions(false);

      // Clear selected files
      setSelectedFiles([]);

      // Reset processing flags
      introMessageProcessingRef.current = false;
      lastProcessedChunk.current = "";
    }

    // Update previous auth state
    previousAuthStateRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const setCurrentSession = useCallback((sessionId: string | null) => {
    setState((prev) => ({ ...prev, currentSessionId: sessionId }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }));
  }, []);

  const setLoadingSession = useCallback((isLoadingSession: boolean) => {
    setState((prev) => ({ ...prev, isLoadingSession }));
  }, []);

  const setStreaming = useCallback((isStreaming: boolean) => {
    setState((prev) => ({ ...prev, isStreaming }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const addMessage = useCallback((message: ChatMessage) => {
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
  }, []);

  const lastProcessedChunk = useRef<string>("");

  const updateLastMessage = useCallback((content: string) => {
    setState((prev) => {
      const messages = [...prev.messages];
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.role === "assistant") {
        // Prevent only immediate consecutive duplicates (React StrictMode protection)
        // But allow same content to appear later in the stream
        if (content === lastProcessedChunk.current) {
          return prev;
        }

        // Track this chunk as the last processed
        lastProcessedChunk.current = content;

        // Append content
        lastMessage.content += content;
        lastMessage.isStreaming = true;
      }

      return { ...prev, messages };
    });
  }, []);

  const finalizeLastMessage = useCallback(() => {
    setState((prev) => {
      const messages = [...prev.messages];
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.role === "assistant") {
        lastMessage.isStreaming = false;
      }

      return { ...prev, messages };
    });
  }, []);

  // Finalize a specific message by ID (called when frontend streaming completes)
  const finalizeMessageStreaming = useCallback((messageId: string) => {
    // Clear any pending finalization timeout since we're finalizing now
    if (finalizationTimeoutRef.current) {
      clearTimeout(finalizationTimeoutRef.current);
      finalizationTimeoutRef.current = null;
    }

    setState((prev) => {
      const messages = [...prev.messages];
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);

      if (messageIndex !== -1 && messages[messageIndex].role === "assistant") {
        messages[messageIndex] = {
          ...messages[messageIndex],
          isStreaming: false,
        };
      }

      return { ...prev, messages };
    });
  }, []);

  // New: Delayed finalization to allow frontend streaming to complete
  const finalizeLastMessageDelayed = useCallback(
    (delay: number = 2000) => {
      // Clear any existing timeout
      if (finalizationTimeoutRef.current) {
        clearTimeout(finalizationTimeoutRef.current);
      }

      // Set new timeout and store reference
      finalizationTimeoutRef.current = setTimeout(() => {
        finalizeLastMessage();
        finalizationTimeoutRef.current = null;
      }, delay);
    },
    [finalizeLastMessage]
  );

  const markMessageAsFailed = useCallback((messageId: string) => {
    setState((prev) => {
      const messages = [...prev.messages];
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);

      if (messageIndex !== -1) {
        messages[messageIndex] = {
          ...messages[messageIndex],
          isStreaming: false,
          failed: true,
        };
      }

      return { ...prev, messages };
    });
  }, []);

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreaming(false);
    finalizeLastMessage();
  }, [setStreaming, finalizeLastMessage]);

  const createNewSession = useCallback(() => {
    // Stop any ongoing streaming when creating new session
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      messages: [],
      currentSessionId: null,
      currentDbSessionId: null,
      error: null,
      isStreaming: false,
    }));

    // Clear selected files when creating new session
    setSelectedFiles([]);
  }, []);

  const clearMessages = useCallback(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      error: null,
    }));
  }, []);

  const createFakeSession = useCallback((sessionId: string) => {
    const now = new Date();

    const fakeSession: ChatSession = {
      id: parseInt(sessionId), // Use the streaming session ID converted to number
      user_id: "current-user", // Placeholder
      session_title: t("features.chat.sidebar.newChat"),
      total_tokens: 0,
      deleted: false,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };

    // Add to fake sessions (avoid duplicates)
    setFakeSessions((prev) => {
      const exists = prev.some((s) => s.id === fakeSession.id);
      if (exists) return prev;
      return [fakeSession, ...prev]; // Add to beginning for most recent first
    });
  }, []);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated || isLoadingSessions || disclaimerBlocksChat) return;

    try {
      setIsLoadingSessions(true);
      const sessionsData = await chatApi.getSessions();
      setSessions(sessionsData);

      // Remove fake sessions that now have real counterparts
      setFakeSessions((prevFakes) => {
        const realSessionIds = new Set(
          sessionsData.map((s: ChatSession) => s.id)
        );
        const filteredFakes = prevFakes.filter(
          (fakeSession) => !realSessionIds.has(fakeSession.id)
        );

        if (filteredFakes.length !== prevFakes.length) {
        }

        return filteredFakes;
      });
    } catch (error) {
      // Get translated error message from backend
      const apiError = error as ApiError;
      if (apiError.backendData?.error_key === "ORG_DISCLAIMER_REQUIRED") {
        // Gate owns this state; do not surface a destructive toast.
        return;
      }
      const errorMessage = apiError.backendData
        ? translateBackendMessage(apiError.backendData, t)
        : t("common.error");

      console.error("❌ Failed to load sessions:", errorMessage);
      showToast({
        title: t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoadingSessions(false);
    }
  }, [disclaimerBlocksChat, isAuthenticated, showToast, t]);

  const loadSession = useCallback(
    async (sessionId: string) => {
      if (!isAuthenticated) return;

      try {
        setLoadingSession(true);
        setError(null);

        // Stop any ongoing streaming when loading a session
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }

        // Parse session ID to get database session ID
        const dbSessionId = parseInt(sessionId);

        // Load session metadata from database

        // Try to load message history
        const messageHistory = await chatApi.getSessionMessages(dbSessionId);

        setState((prev) => ({
          ...prev,
          currentDbSessionId: dbSessionId,
          currentSessionId: dbSessionId.toString(), // Set session ID to continue conversation in this session
          messages: messageHistory, // Load actual message history from transcript endpoint
          isStreaming: false,
        }));
      } catch (error) {
        // Get translated error message from backend
        const apiError = error as ApiError;
        if (apiError.backendData?.error_key === "ORG_DISCLAIMER_REQUIRED") {
          setLoadingSession(false);
          return;
        }
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.error");

        setError(errorMessage);
        showToast({
          title: t("common.error"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoadingSession(false);
      }
    },
    [isAuthenticated, showToast, setError, setLoadingSession, t]
  );

  const sendMessage = useCallback(
    async (
      message: string,
      fileIds?: string[],
      forceNewSession: boolean = false,
      answerForm?: AnswerForm
    ) => {
      if (!isAuthenticated) {
        showToast({
          title: "Authentication Required",
          description: "Please log in to send messages",
          variant: "destructive",
        });
        return;
      }

      if (!message.trim() && selectedFiles.length === 0) return;

      // Stop all voice activities (playback and recording) when sending a message
      voiceCoordinator.stopAll();

      // Stop any ongoing streaming
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      let finalFileIds: string[] = fileIds || [];
      // If forceNewSession is true, always use null to create a new session
      // Otherwise, use the current session ID (or null if no session exists)
      let sessionIdForChat = forceNewSession ? null : state.currentSessionId;

      try {
        setError(null);
        setLoading(true); // Initial loading while setting up the request

        // STEP 1: Upload files if there are any selected and fileIds not provided
        if (selectedFiles.length > 0 && !fileIds) {
          // Set all files to uploading status
          setSelectedFiles((prev) =>
            prev.map((file) => ({ ...file, status: "uploading" }))
          );

          try {
            // Determine session ID to use for file upload:
            // Case 1: Force new session - send null to create new session
            // Case 2: New chat (no messages, no session) - send null to create new session
            // Case 3: Existing chat - send current session ID
            const hasMessages = state.messages.length > 0;
            const sessionIdToUse = forceNewSession
              ? null
              : hasMessages && state.currentSessionId
              ? state.currentSessionId
              : null;

            // Upload files to backend
            const files = selectedFiles.map((sf) => sf.file);
            const response: MultipleFileUploadResponse =
              await chatApi.uploadMultipleFilesToSession(files, sessionIdToUse);

            // Extract file IDs from response
            finalFileIds = response.files.map((f) => f.file.id);

            // Handle session creation for new chats
            // If forceNewSession is true, or if no session exists, use the new session from file upload
            if (
              (forceNewSession || !state.currentSessionId) &&
              response.files.length > 0
            ) {
              const newSessionId = response.files[0].file.session_id;
              sessionIdForChat = newSessionId.toString();

              // Update state with new session ID
              setState((prev) => ({
                ...prev,
                currentDbSessionId: newSessionId,
                currentSessionId: newSessionId.toString(),
              }));

              // Create fake session for sidebar display
              createFakeSession(newSessionId.toString());
            }

            // Update files to uploaded status
            setSelectedFiles((prev) =>
              prev.map((file) => ({
                ...file,
                status: "uploaded",
                uploadedFileId: finalFileIds.find((id) => id), // Simple mapping for now
              }))
            );

            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (uploadError) {
            console.error("❌ File upload failed:", uploadError);

            // Get translated error message from backend
            const apiError = uploadError as ApiError;
            const errorMessage = apiError.backendData
              ? translateBackendMessage(apiError.backendData, t)
              : t("common.error");

            // Update files to error status
            setSelectedFiles((prev) =>
              prev.map((file) => ({
                ...file,
                status: "error",
                error: errorMessage,
              }))
            );

            showToast({
              title: t("common.error"),
              description: errorMessage,
              variant: "destructive",
            });

            setLoading(false);
            return; // Stop here if file upload fails
          }
        }

        // STEP 2: Add user message immediately (unless it should be hidden)
        const messageContent =
          message.trim() || t("features.chat.main.filesAttached");
        const shouldHideUserMessage = shouldHideMessage(messageContent);
        const userMessageId = Date.now().toString();

        if (!shouldHideUserMessage) {
          const userMessage: ChatMessage = {
            id: userMessageId,
            role: "user",
            content: messageContent,
            timestamp: new Date(),
            session_id: sessionIdForChat || "",
            ...(answerForm ? { answer_form: answerForm } : {}),
          };

          addMessage(userMessage);
        }

        // Initialize assistant message
        const assistantMessageId = (Date.now() + 1).toString();
        const initialAssistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          timestamp: new Date(),
          session_id: sessionIdForChat || "",
          isStreaming: true,
        };

        addMessage(initialAssistantMessage);

        // STEP 3: Start streaming chat with uploaded file IDs
        // If forceNewSession is true, explicitly pass null to ensure a new session is created
        const sessionIdToSend = forceNewSession ? null : sessionIdForChat;
        const finalSessionId = await chatApi.streamChat(
          message.trim() || t("features.chat.main.filesAttached"),
          sessionIdToSend, // Use null if forcing new session, otherwise use sessionIdForChat
          finalFileIds.length > 0 ? finalFileIds : undefined,

          // onMessage callback - handle each streaming chunk
          (chunk: ChatStreamChunk) => {
            // Once we receive the first chunk, we're now streaming
            setLoading(false);
            setStreaming(true);

            // IMPORTANT: Only process assistant messages from the stream
            // User messages are already added locally, so skip them to prevent duplication
            if (chunk.role === "assistant") {
              // Backend already includes proper spacing in chunk.content
              // Just append the content directly without adding extra spaces
              updateLastMessage(chunk.content);
            }
            // Skip user messages from the stream to prevent duplication
          },

          // onError callback
          (error) => {
            console.error("Streaming error:", error);

            // Don't show error if request was aborted (user stopped streaming)
            if (error instanceof Error && error.name === "AbortError") {
              return;
            }

            // Don't show generic error toast for subscription expired (430)
            // The subscriptionExpiredCallback already handles toast + redirect
            const apiError = error as ApiError;
            if (apiError.code === ERROR_CODES.SUBSCRIPTION_EXPIRED) {
              setStreaming(false);
              if (!shouldHideUserMessage) markMessageAsFailed(userMessageId);
              markMessageAsFailed(assistantMessageId);
              return;
            }

            // Get translated error message from backend
            const errorMessage = apiError.backendData
              ? translateBackendMessage(apiError.backendData, t)
              : apiError.message || t("common.error");

            setError(errorMessage);
            setStreaming(false);
            if (!shouldHideUserMessage) markMessageAsFailed(userMessageId);
            markMessageAsFailed(assistantMessageId);
            showToast({
              title: t("common.error"),
              description: errorMessage,
              variant: "destructive",
            });
          },

          // onComplete callback
          (sessionId: string) => {
            // Update current session ID if it was created
            // This happens when: forcing new session, or no session existed before
            const wasNewSession =
              forceNewSession || (!sessionIdForChat && !state.currentSessionId);
            if (wasNewSession && sessionId) {
              setCurrentSession(sessionId);
              setState((prev) => ({
                ...prev,
                currentDbSessionId: parseInt(sessionId),
              }));

              // Create fake session for immediate sidebar display
              // Always create fake session for new sessions (questionnaire completion, etc.)
              createFakeSession(sessionId);
            }

            loadSessions();

            // Clear selected files after successful send
            setSelectedFiles([]);

            // Stop streaming immediately (backend is done)
            setStreaming(false);

            // IMPORTANT: Delay message finalization to allow frontend streaming animations to complete
            // This prevents the race condition where backend completes before frontend animation finishes
            // Frontend will call finalizeMessageStreaming() immediately when done, canceling this timeout
            finalizeLastMessageDelayed(500); // Fallback timeout (500ms) - frontend should complete before this
          },

          // Abort signal
          abortControllerRef.current?.signal,
          answerForm
        );

        // Update session ID if it was newly created and we don't have one from file upload
        // This handles the case where a new session was created but not yet set in state
        if (
          (forceNewSession || (!sessionIdForChat && !state.currentSessionId)) &&
          finalSessionId
        ) {
          setCurrentSession(finalSessionId);
          setState((prev) => ({
            ...prev,
            currentDbSessionId: parseInt(finalSessionId),
          }));
          // Create fake session for sidebar display
          createFakeSession(finalSessionId);
        }
      } catch (error) {
        // Don't show error if request was aborted (user stopped streaming)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        // Don't show generic error toast for subscription expired (430)
        // The subscriptionExpiredCallback already handles toast + redirect
        const apiError = error as ApiError;
        if (apiError.code === ERROR_CODES.SUBSCRIPTION_EXPIRED) {
          setStreaming(false);
          return;
        }

        // Get translated error message from backend
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : apiError.message || t("common.error");

        setError(errorMessage);
        setStreaming(false);

        showToast({
          title: t("common.error"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [
      isAuthenticated,
      selectedFiles,
      state.currentSessionId,
      state.messages.length,
      showToast,
      setError,
      setLoading,
      setStreaming,
      setCurrentSession,
      addMessage,
      updateLastMessage,
      finalizeLastMessage,
      finalizeLastMessageDelayed,
      markMessageAsFailed,
      createFakeSession,
      loadSessions,
      setSelectedFiles,
      setState,
      t,
    ]
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const failedMessage = state.messages.find(
        (msg) => msg.id === messageId && msg.failed
      );
      if (!failedMessage || failedMessage.role !== "user") return;

      // Find the corresponding assistant message (should be the next one)
      const messageIndex = state.messages.findIndex(
        (msg) => msg.id === messageId
      );
      const assistantMessage = state.messages[messageIndex + 1];

      if (assistantMessage && assistantMessage.role === "assistant") {
        // Remove the failed assistant message
        setState((prev) => ({
          ...prev,
          messages: prev.messages.filter(
            (msg) => msg.id !== assistantMessage.id
          ),
        }));
      }

      // Clear the failed status from the user message
      setState((prev) => {
        const messages = [...prev.messages];
        const msgIndex = messages.findIndex((msg) => msg.id === messageId);
        if (msgIndex !== -1) {
          messages[msgIndex] = {
            ...messages[msgIndex],
            failed: false,
          };
        }
        return { ...prev, messages };
      });

      // Retry sending the message
      await sendMessage(
        failedMessage.content,
        undefined,
        false,
        buildStreamAnswerForm({
          menuEnabled: isAnswerFormMenuEnabled(user?.user.organization),
          chip: failedMessage.answer_form,
        })
      );
    },
    [state.messages, sendMessage, user?.user.organization]
  );

  const loadMockSession = useCallback(() => {
    const mockMessages = [
      {
        id: "mock-1",
        role: "user" as const,
        content:
          "I feeling great today, why do I feel guilty about it? I should be happy but there's this underlying feeling that I don't deserve to feel good.",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        isStreaming: false,
        session_id: "999",
      },
      {
        id: "mock-2",
        role: "assistant" as const,
        content:
          "It sounds like you're experiencing something quite complex - feeling good but then questioning whether you deserve that happiness. This kind of guilt around positive emotions is more common than you might think.\n\nCan you tell me more about what specifically triggers this guilty feeling when you're happy? Is there a particular voice in your head or a specific thought that comes up?",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 2 * 60 * 1000), // 2 hours ago + 2 minutes
        isStreaming: false,
        session_id: "999",
      },
      {
        id: "mock-3",
        role: "user" as const,
        content:
          "I think it comes from my childhood. My family went through some really tough financial times, and I remember my parents always being stressed. I feel like if I'm too happy, I'm being insensitive to their struggles, even though that was years ago.",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 5 * 60 * 1000), // 2 hours ago + 5 minutes
        isStreaming: false,
        session_id: "999",
      },
      {
        id: "mock-4",
        role: "assistant" as const,
        content:
          "Thank you for sharing that. It makes a lot of sense that those formative experiences would shape how you relate to your own happiness. You developed a deep empathy and awareness during those difficult times, which shows your caring nature.\n\nIt sounds like part of you learned that being happy might somehow diminish or disrespect the pain others experienced. But I'm curious - do you think your parents would want you to carry that guilt now? What would they say if they knew you were limiting your joy because of their past struggles?",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 7 * 60 * 1000), // 2 hours ago + 7 minutes
        isStreaming: false,
        session_id: "999",
      },
    ];

    setState((prev) => ({
      ...prev,
      messages: mockMessages,
      currentSessionId: "999",
      currentDbSessionId: 999,
      error: null,
    }));
  }, []);

  const startChatWithIntroMessage = useCallback(
    async (introMessage: string, forceNewSession: boolean = false) => {
      if (!isAuthenticated) {
        showToast({
          title: "Authentication Required",
          description: "Please log in to start chat",
          variant: "destructive",
        });
        return;
      }

      // Prevent duplicate calls if already processing an intro message
      if (introMessageProcessingRef.current) {
        return;
      }

      // Prevent duplicate calls if already loading or streaming
      if (state.isLoading || state.isStreaming) {
        return;
      }

      try {
        introMessageProcessingRef.current = true;

        // If forceNewSession is true, create a new session first
        // This ensures the intro message goes to a fresh session, not an existing one
        if (forceNewSession) {
          createNewSession();
        }

        // Send the intro message to the backend via the regular sendMessage flow
        // Pass forceNewSession to ensure a new session is created even if state hasn't updated yet
        await sendMessage(introMessage, undefined, forceNewSession);
      } catch (error) {
        const apiError = error as ApiError;
        const errorMessage = apiError.backendData
          ? translateBackendMessage(apiError.backendData, t)
          : t("common.error");
        console.error("Failed to start chat with intro message:", errorMessage);
        setError(errorMessage);
        showToast({
          title: t("common.error"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        introMessageProcessingRef.current = false;
      }
    },
    [
      isAuthenticated,
      showToast,
      sendMessage,
      setError,
      state.isLoading,
      state.isStreaming,
      createNewSession,
    ]
  );

  // File management functions
  const addFiles = useCallback((files: File[]) => {
    // Add files to local state as "selected"
    const newSelectedFiles: LocalUploadedFile[] = files.map((file) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file, // Store the original file for later upload
      status: "selected" as const,
    }));

    setSelectedFiles((prev) => [...prev, ...newSelectedFiles]);
  }, []);

  const removeSelectedFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const clearSelectedFiles = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const setFileStatus = useCallback(
    (fileId: string, status: LocalUploadedFile["status"], error?: string) => {
      setSelectedFiles((prev) =>
        prev.map((file) =>
          file.id === fileId ? { ...file, status, error } : file
        )
      );
    },
    []
  );

  // Combine real sessions and fake sessions
  // Real sessions take priority, fake sessions are only shown if no real session with same ID exists
  const combinedSessions = [...sessions, ...fakeSessions];

  // Filter out messages that should be hidden (both user and assistant messages)
  const filteredMessages = state.messages.filter(
    (message) => !shouldHideMessage(message.content)
  );

  return (
    <ChatContext.Provider
      value={{
        messages: filteredMessages,
        currentSessionId: state.currentSessionId,
        currentDbSessionId: state.currentDbSessionId,
        isLoading: state.isLoading,
        isLoadingSession: state.isLoadingSession,
        isStreaming: state.isStreaming,
        error: state.error,
        sendMessage,
        retryMessage,
        stopStreaming,
        clearMessages,
        setCurrentSession,
        finalizeMessageStreaming,
        createNewSession,
        loadSession,
        loadMockSession,
        startChatWithIntroMessage,
        sessions: combinedSessions,
        loadSessions,
        selectedFiles,
        addFiles,
        removeSelectedFile,
        clearSelectedFiles,
        setFileStatus,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
